/**
 * V2EdgeCleaner - Utility for clearing all edges from V2 StepNode entities
 *
 * Purpose:
 * When cloning or creating V2 StepNodes from templates, we need to clear all
 * transitions/edges to create a "clean" copy without any connections.
 *
 * Unlike V1 which uses dynamic ScreenHandleResolver discovery, V2 edge locations
 * are static and well-defined (10 confirmed + 2 defensive from type definitions).
 *
 * Architecture:
 * - V2 edges live inside Trigger objects at known content paths
 * - Each Trigger has { kind: ActionKind, edges: Edge[] }
 * - Countdown objects (carousel.timer, stepper.timer, countdown) wrap Trigger via .action
 * - EmbedConfig wraps Trigger via .callback.action
 *
 * Related: EdgeCleaner (V1), V2TransitionManager, V2ScreenAdapter
 */

import type { StepNode } from '@segmently/schema-models/v2';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function clearSectionEmbeddedEdges(section: any): void {
  if (section?.buttonContent?.action?.edges) {
    section.buttonContent.action.edges = [];
  }
  if (section?.buttonContent?.action?.onCancelAction?.edges) {
    section.buttonContent.action.onCancelAction.edges = [];
  }
  section?.optionsListContent?.items?.forEach((item: any) => {
    if (item.action?.edges) {
      item.action.edges = [];
    }
  });
}

/**
 * Clear all edges from a V2 StepNode entity
 *
 * V2 StepNode has edges at 10 confirmed locations (from type definitions):
 * - actionBar.primary/secondary (TapTarget → Trigger → edges)
 * - header.skip/back (TapTarget → Trigger → edges)
 * - options.items[N] (OptionsItem → Trigger → edges)
 * - carousel.timer (Countdown → Trigger → edges)
 * - stepper.timer (Countdown → Trigger → edges)
 * - countdown (Countdown → Trigger → edges)
 * - systemPrompt (SystemPrompt → Trigger → edges)
 * - embed.callback (EmbedCallback → Trigger → edges)
 *
 * Plus 2 defensive locations (not in type definitions but may exist at runtime):
 * - actionBar.purchase
 * - header.close
 *
 * @param step - Source StepNode to clear edges from
 * @returns New StepNode instance with all edges cleared
 */
export function clearAllStepEdges(step: StepNode): StepNode {
  // Deep clone to avoid mutating original
  const cleaned: StepNode = JSON.parse(JSON.stringify(step));
  const c = cleaned.content;

  if (!c) {
    return cleaned;
  }

  // ActionBar buttons (2 confirmed + 1 defensive)
  if (c.actionBar?.primary?.action?.edges) {
    c.actionBar.primary.action.edges = [];
  }
  if (c.actionBar?.secondary?.action?.edges) {
    c.actionBar.secondary.action.edges = [];
  }
  // Defensive: purchase not in ActionBar.d.ts but may exist at runtime
  if ((c.actionBar as any)?.purchase?.action?.edges) {
    (c.actionBar as any).purchase.action.edges = [];
  }

  // Header buttons (2 confirmed + 1 defensive)
  if (c.header?.skip?.action?.edges) {
    c.header.skip.action.edges = [];
  }
  if (c.header?.back?.action?.edges) {
    c.header.back.action.edges = [];
  }
  // Defensive: close not in HeaderBar.d.ts but may exist at runtime
  if ((c.header as any)?.close?.action?.edges) {
    (c.header as any).close.action.edges = [];
  }

  // Options items (dynamic count)
  if (c.options?.items) {
    c.options.items.forEach(item => {
      if (item.action?.edges) {
        item.action.edges = [];
      }
    });
  }

  // Carousel timer (CarouselConfig.timer: Countdown → action: Trigger)
  if (c.carousel?.timer?.action?.edges) {
    c.carousel.timer.action.edges = [];
  }

  // Stepper timer (StepperConfig.timer: Countdown → action: Trigger)
  if (c.stepper?.timer?.action?.edges) {
    c.stepper.timer.action.edges = [];
  }

  // Screen-level countdown (StepContent.countdown: Countdown → action: Trigger)
  if (c.countdown?.action?.edges) {
    c.countdown.action.edges = [];
  }

  // SystemPrompt (SystemPrompt.action: Trigger)
  if (c.systemPrompt?.action?.edges) {
    c.systemPrompt.action.edges = [];
  }

  // Embed callback (EmbedConfig.callback: EmbedCallback → action: Trigger)
  if (c.embed?.callback?.action?.edges) {
    c.embed.callback.action.edges = [];
  }
  if ((c.embed as any)?.childSections) {
    ((c.embed as any).childSections as any[]).forEach(clearSectionEmbeddedEdges);
  }

  // V2 Paywall-specific buttons
  if ((c as any).paywall?.closeButton?.action?.edges) {
    (c as any).paywall.closeButton.action.edges = [];
  }
  if ((c as any).paywall?.purchaseAction?.action?.edges) {
    (c as any).paywall.purchaseAction.action.edges = [];
  }
  // Downsale: clear onCancelAction edges on purchase button
  if ((c as any).paywall?.purchaseAction?.action?.onCancelAction?.edges) {
    (c as any).paywall.purchaseAction.action.onCancelAction.edges = [];
  }

  // FlexibleLayout sections
  if (c.flexibleLayout?.sections) {
    (c.flexibleLayout.sections as any[]).forEach((section: any) => {
      if (section.buttonContent?.action?.edges) {
        section.buttonContent.action.edges = [];
      }
      // Downsale: clear onCancelAction edges on button sections
      if (section.buttonContent?.action?.onCancelAction?.edges) {
        section.buttonContent.action.onCancelAction.edges = [];
      }
      if (section.embedContent?.callback?.action?.edges) {
        section.embedContent.callback.action.edges = [];
      }
      clearSectionEmbeddedEdges(section);
      if (section.embedContent?.childSections) {
        (section.embedContent.childSections as any[]).forEach(clearSectionEmbeddedEdges);
      }
      if (section.paywallHeaderContent?.closeButton?.action?.edges) {
        section.paywallHeaderContent.closeButton.action.edges = [];
      }
    });
  }

  return cleaned;
}
